// @flow
import * as React from 'react';
import ReactDOM from 'react-dom';
import config from './config';
import { Router, Route, IndexRoute } from 'react-router';
import { Provider } from 'react-redux';
import store, { history } from './store';
import { persistStore } from 'redux-persist';
import Raven from 'raven-js';
import 'classlist.js';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/css/bootstrap-theme.css';
import Bookings from './containers/bookings/Bookings';
import Campaigns from './containers/campaigns/Campaigns';
import CampaignFinaliseAMBooking from './containers/campaigns/CampaignFinaliseAMBooking';
import Customers from './containers/customer/Customers';
import CustomerDetails from './containers/customer/CustomerDetails';
import CustomerAgencies from './containers/customer/CustomerAgencies';
import CustomerLocations from './containers/customer/CustomerLocations';
import CustomerUsers from './containers/customer/CustomerUsers';
import CustomerShapeSets from './containers/customer/CustomerShapeSets';
import CustomerInsightSets from './containers/customer/CustomerInsightSets';
import CreateNewCustomer from './containers/customer/CreateNewCustomer';
import Locations from './containers/location/Locations';
import LocationDetails from './containers/location/LocationDetails';
import LocationUsers from './containers/location/LocationUsers';
import InsightSetDetails from './containers/insightset/InsightSetDetails';
import InsightSetInsights from './containers/insightset/InsightSetInsights';
import InsightSetOrganisations from './containers/insightset/InsightSetOrganisations';
import InsightSetIndustries from './containers/insightset/InsightSetIndustries';
import InsightSets from './containers/insightset/InsightSets';
import IndustryDetails from './containers/industries/IndustryDetails';
import IndustryInsightSets from './containers/industries/IndustryInsightSets';
import IndustryOrganisations from './containers/industries/IndustryOrganisations';
import IndustryShapeSets from './containers/industries/IndustryShapeSets';
import Industries from './containers/industries/Industries';
import ReferenceLayers from './containers/referenceLayers/ReferenceLayers';
import ReferenceLayerDetails from './containers/referenceLayers/ReferenceLayerDetails';
import ShapeSets from './containers/shapeset/ShapeSets';
import Users from './containers/users/Users';
import UserDetails from './containers/users/UserDetails';
import UserAccounts from './containers/users/UserAccounts';
import UserCustomers from './containers/users/UserCustomers';
import UserInvitation from './containers/users/UserInvitation';
import ShapeSetDetails from './containers/shapeset/ShapeSetDetails';
import ShapeSetShapes from './containers/shapeset/ShapeSetShapes';
import ShapeSetOrganisations from './containers/shapeset/ShapeSetOrganisations';
import ShapeSetIndustries from './containers/shapeset/ShapeSetIndustries';
import Account from './containers/account/Account';
import VerifyEmail from './containers/account/VerifyEmail';
import ForgotPassword from './containers/account/ForgotPassword';
import ResetPassword from './containers/account/ResetPassword';
import UpdatePassword from './containers/account/UpdatePassword';
import Register from './containers/Register';
import Login from './containers/Login';
import Welcome from './containers/Welcome';
import SettingsGeneral from './containers/settings/SettingsGeneral';
import SettingsEmail from './containers/settings/SettingsEmail';
import SettingsHelpLinks from './containers/settings/SettingsHelpLinks';
import SettingsUMBookingConfig from './containers/settings/SettingsUMBookingConfig';
import SettingsPageContent from './containers/settings/SettingsPageContent';
import SettingsTermsAndConditions from './containers/settings/SettingsTermsAndConditions';
import SettingsAMBooking from './containers/settings/SettingsAMBooking';
import NotFound from './containers/NotFound';
import Dashboard from './containers/Dashboard';
import App from './App';
import MapContainer from './containers/MapContainer';
import LoadingSpinner from './components/LoadingSpinner';
import { orgsCustomerscompressor } from './helpers';
import './styles/app.css';

if (process.env.NODE_ENV === 'production') {
  // Error Logging
  const env = process.env.REACT_APP_ENV;
  Raven.config(config.sentryDSN, {
    environment: env ? env : ''
  }).install();
}

type Props = {};

type State = {
  rehydrated: boolean
};

class Root extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      rehydrated: false
    };
  }

  componentWillMount() {
    persistStore(
      store,
      {
        transforms: [orgsCustomerscompressor],
        blacklist: ['routing']
      },
      () => {
        this.setState({ rehydrated: true });
      }
    );
  }
  render() {
    const campaignName = 'Manage Campaigns';
    const bookingName = 'Manage Bookings';
    const customersName = 'Manage Customers';
    const usersName = 'Manage Users';
    const subAccountsName = 'Manage Sub-Accounts';
    const insightSetsName = 'Manage Insight Sets';
    const industriesName = 'Manage Industries';
    const referenceLayersName = 'Manage Reference Layers';
    const catchmentSetsName = 'Manage Catchment Sets';

    if (!this.state.rehydrated) {
      return (
        <div>
          <LoadingSpinner />
        </div>
      );
    }
    return (
      <Provider store={store}>
        <Router history={history}>
          {/* Pages without App layout */}
          <Route path="/mds" />

          {/* PUBLIC ACCOUNT STUFF */}
          <Route path="/register" component={Register} name="Register" />
          <Route
            path="/verify-email"
            component={VerifyEmail}
            name="Verify Email"
          />
          <Route
            path="/forgot-password"
            component={ForgotPassword}
            name="Forgot Password"
          />
          <Route
            path="/reset-password"
            component={ResetPassword}
            name="Reset Password"
          />
          <Route path="/welcome" component={Welcome} name="Welcome" />

          {/* MAIN APP */}
          <Route path="/" component={App}>
            <IndexRoute
              component={Dashboard}
              name="Dashboard"
              context="global"
            />
            <Route path="/login" component={Login} name="Login" />

            {/* CAMPAIGNS */}
            <Route
              path="/:context/:organisationId/campaigns"
              component={Campaigns}
              name={campaignName}
            />
            <Route
              path="/:context/:organisationId/campaigns/:campaignId"
              component={Campaigns}
              name={campaignName}
              step="1"
            />
            <Route
              path="/:context/:organisationId/campaigns/:campaignId/map"
              component={MapContainer}
              name={campaignName}
              step="2"
            />
            <Route
              path="/:context/:organisationId/campaigns/:campaignId/review"
              component={MapContainer}
              name={campaignName}
              step="3"
            />
            <Route
              path="/:context/:organisationId/campaigns/:campaignId/book"
              component={MapContainer}
              name={campaignName}
              step="4"
            />

            <Route
              path="/:context/:organisationId/campaigns/:campaignId/finalise-am-booking"
              component={CampaignFinaliseAMBooking}
              name={campaignName}
              step="5"
            />

            {/* BOOKINGS */}
            <Route
              path="/:context/:organisationId/bookings"
              component={Bookings}
              name={bookingName}
              context="global"
            />
            <Route
              path="/:context/:organisationId/bookings/:bookingId"
              component={Bookings}
              name={bookingName}
              step="1"
            />

            {/* CUSTOMERS */}
            <Route
              path="/customers"
              component={Customers}
              name={customersName}
              context="global"
            >
              <Route
                path="/customers/:customerId"
                component={CustomerDetails}
                name={customersName}
                context="global"
              />
              <Route
                path="/customers/:customerId/agencies"
                component={CustomerAgencies}
                name={customersName}
                context="global"
              />
              <Route
                path="/customers/:customerId/sub-account-list"
                component={CustomerLocations}
                name={customersName}
                context="global"
              />
              <Route
                path="/customers/:customerId/userList"
                component={CustomerUsers}
                name={customersName}
                context="global"
              />
              <Route
                path="/customers/:customerId/catchment-set-list"
                component={CustomerShapeSets}
                name={customersName}
                context="global"
              />
              <Route
                path="/customers/:customerId/insight-sets"
                component={CustomerInsightSets}
                name={customersName}
                context="global"
              />
            </Route>

            {/* CREATE CUSTOMER */}
            <Route
              path="/customer/create"
              component={CreateNewCustomer}
              name="Create New Customer"
            />

            {/* SINGLE CUSTOMER */}
            <Route
              path="/customer"
              component={Customers}
              name={customersName}
              context="customer"
            >
              <Route
                path="/customer/:customerId"
                component={CustomerDetails}
                name={customersName}
                context="customer"
              />
              <Route
                path="/customer/:customerId/agencies"
                component={CustomerAgencies}
                name={customersName}
                context="customer"
              />
              <Route
                path="/customer/:customerId/sub-account-list"
                component={CustomerLocations}
                name={customersName}
                context="customer"
              />
              <Route
                path="/customer/:customerId/userList"
                component={CustomerUsers}
                name={customersName}
                context="customer"
              />
              <Route
                path="/customer/:customerId/catchment-set-list"
                component={CustomerShapeSets}
                name={customersName}
                context="customer"
              />
              <Route
                path="/customer/:customerId/insight-sets"
                component={CustomerInsightSets}
                name={customersName}
                context="customer"
              />
            </Route>

            {/* USERS */}
            <Route
              path="/:context/:organisationId/users"
              component={Users}
              name={usersName}
              context="customer"
            >
              <Route
                path="/:context/:organisationId/users/invite"
                component={UserInvitation}
                name={usersName}
                context="customer"
              />

              <Route
                path="/:context/:organisationId/users/:userId"
                component={UserDetails}
                name={usersName}
                context="customer"
              />

              <Route
                path="/:context/:organisationId/users/:userId/accounts"
                component={UserAccounts}
                name={usersName}
              />

              <Route
                path="/:context/:organisationId/users/:userId/customers"
                component={UserCustomers}
                name={usersName}
              />
            </Route>

            {/* LOCATIONS / SUB-ACCOUNTS */}
            <Route
              path="/:context/:customerId/sub-accounts"
              component={Locations}
              name={subAccountsName}
            >
              <Route
                path="/:context/:customerId/sub-accounts/:locationId"
                component={LocationDetails}
                name={subAccountsName}
              />
              <Route
                path="/:context/:customerId/sub-accounts/:locationId/userList"
                component={LocationUsers}
                name={subAccountsName}
              />
            </Route>

            {/* INSIGHT SETS */}
            <Route
              path="/insight-sets"
              component={InsightSets}
              name={insightSetsName}
            >
              <Route
                path="/insight-sets/:insightSetId"
                component={InsightSetDetails}
                name={insightSetsName}
              />
              <Route
                path="/insight-sets/:insightSetId/insights"
                component={InsightSetInsights}
                name={insightSetsName}
              />
              <Route
                path="/insight-sets/:insightSetId/organisations"
                component={InsightSetOrganisations}
                name={insightSetsName}
              />
              <Route
                path="/insight-sets/:insightSetId/industries"
                component={InsightSetIndustries}
                name={insightSetsName}
              />
            </Route>

            {/* INDUSTRIES */}
            <Route
              path="/industries"
              component={Industries}
              name={industriesName}
            >
              <Route
                path="/industries/:industryId"
                component={IndustryDetails}
                name={industriesName}
              />
              <Route
                path="/industries/:industryId/insight-sets"
                component={IndustryInsightSets}
                name={industriesName}
              />
              <Route
                path="/industries/:industryId/organisations"
                component={IndustryOrganisations}
                name={industriesName}
              />
              <Route
                path="/industries/:industryId/catchment-sets"
                component={IndustryShapeSets}
                name={industriesName}
              />
            </Route>

            {/* REFERENCE LAYERS */}
            <Route
              path="/reference-layers"
              component={ReferenceLayers}
              name={referenceLayersName}
            >
              <Route
                path="/reference-layers/:referenceLayerId"
                component={ReferenceLayerDetails}
                name={referenceLayersName}
              />
            </Route>

            {/* CATCHMENT SETS */}
            <Route
              path="/:context/:organisationId/catchment-sets"
              component={ShapeSets}
              name={catchmentSetsName}
            >
              <Route
                path="/:context/:organisationId/catchment-sets/:shapeSetId"
                component={ShapeSetDetails}
                name={catchmentSetsName}
              />
              <Route
                path="/:context/:organisationId/catchment-sets/:shapeSetId/shapes"
                component={ShapeSetShapes}
                name={catchmentSetsName}
              />
              <Route
                path="/:context/:organisationId/catchment-sets/:shapeSetId/organisations"
                component={ShapeSetOrganisations}
                name={catchmentSetsName}
              />
              <Route
                path="/:context/:organisationId/catchment-sets/:shapeSetId/industries"
                component={ShapeSetIndustries}
                name={catchmentSetsName}
              />
            </Route>

            {/* SETTINGS */}
            <Route
              path="/settings"
              component={SettingsGeneral}
              name="Settings - General"
            />
            <Route
              path="/settings/general"
              component={SettingsGeneral}
              name="Settings - General"
            />
            <Route
              path="/settings/email"
              component={SettingsEmail}
              name="Settings - Email"
            />
            <Route
              path="/settings/help"
              component={SettingsHelpLinks}
              name="Settings - Help Links"
            />
            <Route
              path="/settings/um-booking"
              component={SettingsUMBookingConfig}
              name="Settings - UM Booking"
            />
            <Route
              path="/settings/pages"
              component={SettingsPageContent}
              name="Settings - Page Content"
            />
            <Route
              path="/settings/terms-and-conditions"
              component={SettingsTermsAndConditions}
              name="Settings - Terms and Conditions"
            />
            <Route
              path="/settings/am-bookings"
              component={SettingsAMBooking}
              name="Settings - AM Bookings"
            />

            {/* MISC ROUTES */}
            <Route path="/account" component={Account} name="My Account" />
            <Route
              path="/account/update-password"
              component={UpdatePassword}
              name="Update Password"
            />

            {/* 404 Catch all */}
            <Route path="*" component={NotFound} />
          </Route>
        </Router>
      </Provider>
    );
  }
}

export default Root;

// $FlowFixMe
ReactDOM.render(<Root />, document.getElementById('root'));



// WEBPACK FOOTER //
// ./src/index.js